import { useState, useCallback } from "react";
import { Calculator, Copy, Delete } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function safeEvaluate(expression: string): number | null {
  // Tokenize: numbers and operators
  const tokens: (number | string)[] = [];
  let current = "";

  for (let i = 0; i < expression.length; i++) {
    const ch = expression[i];
    if ("+-×÷".includes(ch)) {
      if (current) tokens.push(parseFloat(current));
      tokens.push(ch);
      current = "";
    } else if (ch === "." || (ch >= "0" && ch <= "9")) {
      current += ch;
    }
  }
  if (current) tokens.push(parseFloat(current));

  if (tokens.length === 0) return null;
  if (typeof tokens[0] !== "number") return null;

  // First pass: × and ÷
  let intermediate: (number | string)[] = [];
  intermediate.push(tokens[0]);
  for (let i = 1; i < tokens.length; i += 2) {
    const op = tokens[i] as string;
    const right = tokens[i + 1] as number;
    if (right === undefined || typeof right !== "number") return null;

    if (op === "×") {
      const left = intermediate.pop() as number;
      intermediate.push(left * right);
    } else if (op === "÷") {
      if (right === 0) return null;
      const left = intermediate.pop() as number;
      intermediate.push(left / right);
    } else {
      intermediate.push(op, right);
    }
  }

  // Second pass: + and -
  let result = intermediate[0] as number;
  for (let i = 1; i < intermediate.length; i += 2) {
    const op = intermediate[i] as string;
    const right = intermediate[i + 1] as number;
    if (op === "+") result += right;
    else if (op === "-") result -= right;
  }

  return Number.isFinite(result) ? result : null;
}

interface CalculatorDialogProps {
  trigger?: React.ReactNode;
}

export function CalculatorDialog({ trigger }: CalculatorDialogProps) {
  const [expression, setExpression] = useState("0");
  const [hasResult, setHasResult] = useState(false);

  const appendChar = useCallback(
    (ch: string) => {
      setExpression((prev) => {
        if (hasResult && ch >= "0" && ch <= "9") {
          setHasResult(false);
          return ch;
        }
        setHasResult(false);

        if (prev === "0" && ch >= "1" && ch <= "9") return ch;
        if (prev === "0" && ch === "0") return "0";

        // Prevent double operators
        const lastChar = prev[prev.length - 1];
        if ("+-×÷".includes(ch) && "+-×÷".includes(lastChar)) {
          return prev.slice(0, -1) + ch;
        }

        // Prevent double dots in same number
        if (ch === ".") {
          const parts = prev.split(/[+\-×÷]/);
          const lastPart = parts[parts.length - 1];
          if (lastPart.includes(".")) return prev;
        }

        return prev + ch;
      });
    },
    [hasResult]
  );

  const clear = useCallback(() => {
    setExpression("0");
    setHasResult(false);
  }, []);

  const backspace = useCallback(() => {
    setExpression((prev) => {
      if (hasResult) return "0";
      const next = prev.slice(0, -1);
      return next || "0";
    });
    setHasResult(false);
  }, [hasResult]);

  const calculate = useCallback(() => {
    const result = safeEvaluate(expression);
    if (result !== null) {
      // Format: remove trailing zeros but keep up to 2 decimal places
      const formatted =
        Math.abs(result - Math.round(result)) < 0.0000001
          ? Math.round(result).toString()
          : parseFloat(result.toFixed(6)).toString();
      setExpression(formatted);
      setHasResult(true);
    }
  }, [expression]);

  const copyResult = useCallback(() => {
    const result = hasResult ? expression : null;
    const value = result ?? safeEvaluate(expression)?.toString();
    if (value) {
      navigator.clipboard.writeText(value);
      toast.success("Resultado copiado!");
    }
  }, [expression, hasResult]);

  const displayValue = expression;

  const buttons = [
    { label: "C", action: clear, className: "text-destructive font-bold" },
    { label: "⌫", action: backspace, className: "font-bold" },
    { label: "÷", action: () => appendChar("÷"), className: "text-primary font-bold text-lg" },
    { label: "×", action: () => appendChar("×"), className: "text-primary font-bold text-lg" },
    { label: "7", action: () => appendChar("7") },
    { label: "8", action: () => appendChar("8") },
    { label: "9", action: () => appendChar("9") },
    { label: "-", action: () => appendChar("-"), className: "text-primary font-bold text-lg" },
    { label: "4", action: () => appendChar("4") },
    { label: "5", action: () => appendChar("5") },
    { label: "6", action: () => appendChar("6") },
    { label: "+", action: () => appendChar("+"), className: "text-primary font-bold text-lg" },
    { label: "1", action: () => appendChar("1") },
    { label: "2", action: () => appendChar("2") },
    { label: "3", action: () => appendChar("3") },
    { label: "=", action: calculate, className: "bg-primary text-primary-foreground hover:bg-primary/90 row-span-2 font-bold text-lg", rowSpan: true },
    { label: "0", action: () => appendChar("0"), className: "col-span-2" },
    { label: ".", action: () => appendChar(".") },
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="icon" title="Calculadora">
            <Calculator className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-xs p-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Calculator className="h-4 w-4" />
            Calculadora
          </DialogTitle>
        </DialogHeader>

        {/* Display */}
        <div className="rounded-lg border bg-muted/50 p-3 space-y-1">
          <div className="text-xs text-muted-foreground min-h-[1rem] text-right truncate">
            {!hasResult ? expression : ""}
          </div>
          <div className="text-2xl font-mono font-bold text-right truncate">
            {displayValue}
          </div>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-4 gap-1.5">
          {buttons.map((btn, i) => (
            <Button
              key={i}
              variant="outline"
              className={cn(
                "h-12 text-base font-medium",
                btn.className
              )}
              onClick={btn.action}
            >
              {btn.label === "⌫" ? <Delete className="h-4 w-4" /> : btn.label}
            </Button>
          ))}
        </div>

        {/* Copy button */}
        <Button
          variant="secondary"
          size="sm"
          className="w-full gap-2"
          onClick={copyResult}
        >
          <Copy className="h-3.5 w-3.5" />
          Copiar resultado
        </Button>
      </DialogContent>
    </Dialog>
  );
}
